import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox, Bike, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_c6eec58c-238b-4337-9d43-c824e890ecf6/artifacts/f0e7ezix_image_7f0b2b8d.jpg";

const inputCls =
  "w-full bg-[#0a0f1d] border border-white/15 rounded-sm px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#d4af37] transition-colors duration-300";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("majisa_admin_token", data.access_token);
      onLogin(data.user);
      toast.success("Welcome back, Admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-[#0a0f1d] flex items-center justify-center px-5">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm bg-[#0d1424] border border-white/10 rounded-sm p-8"
      >
        <img src={LOGO} alt="Majisa" className="h-12 w-auto rounded-md bg-white/95 px-2 py-1 object-contain mb-8" />
        <h1 className="font-display text-3xl font-extrabold uppercase text-slate-100 mb-1">Admin Desk</h1>
        <p className="text-sm text-slate-500 mb-7">Sign in to view enquiries & applications</p>
        <div className="space-y-4">
          <input data-testid="admin-email-input" type="email" required placeholder="Admin Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <input data-testid="admin-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#d4af37] text-[#0a0f1d] font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-sm hover:bg-[#f59e0b] disabled:opacity-60 transition-colors duration-300"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

const Empty = ({ label }) => (
  <div className="py-16 text-center">
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [eq, ap] = await Promise.all([api.get("/admin/enquiries"), api.get("/admin/applications")]);
      setEnquiries(eq.data);
      setApplications(ap.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("majisa_admin_token");
        setUser(null);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("majisa_admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem("majisa_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0f1d] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  if (!user) return <Login onLogin={setUser} />;

  const logout = () => {
    localStorage.removeItem("majisa_admin_token");
    setUser(null);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0a0f1d]">
      <header className="border-b border-white/10 bg-[#0d1424]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Majisa" className="h-9 w-auto rounded bg-white/95 px-1.5 py-0.5 object-contain" />
            <span className="font-display text-xl font-bold uppercase text-slate-100 hidden sm:inline">Admin Desk</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300 border border-white/15 px-4 py-2 rounded-sm hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-300"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300 border border-white/15 px-4 py-2 rounded-sm hover:border-red-400 hover:text-red-400 transition-colors duration-300"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-8">
          <button
            data-testid="admin-tab-enquiries"
            onClick={() => setTab("enquiries")}
            className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-sm border transition-colors duration-300 ${
              tab === "enquiries" ? "bg-[#d4af37] text-[#0a0f1d] border-[#d4af37]" : "text-slate-400 border-white/15"
            }`}
          >
            <Inbox className="w-4 h-4" /> Client Enquiries ({enquiries.length})
          </button>
          <button
            data-testid="admin-tab-applications"
            onClick={() => setTab("applications")}
            className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-sm border transition-colors duration-300 ${
              tab === "applications" ? "bg-[#3b82f6] text-white border-[#3b82f6]" : "text-slate-400 border-white/15"
            }`}
          >
            <Bike className="w-4 h-4" /> Job Applications ({applications.length})
          </button>
        </div>

        {tab === "enquiries" ? (
          <div data-testid="admin-enquiries-list" className="space-y-3">
            {enquiries.length === 0 && !loading && <Empty label="No client enquiries yet. They will appear here as soon as someone submits the form." />}
            {enquiries.map((e) => (
              <div key={e.id} data-testid={`enquiry-card-${e.id}`} className="bg-[#0d1424] border border-white/10 rounded-sm p-5 grid sm:grid-cols-4 gap-3">
                <div>
                  <p className="text-sm font-bold text-slate-100">{e.name}</p>
                  <p className="text-xs text-slate-500">{e.company || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Contact</p>
                  <a href={`tel:${e.phone}`} className="text-sm text-[#d4af37]">{e.phone}</a>
                  {e.email && <p className="text-xs text-slate-400 break-all">{e.email}</p>}
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Requirement</p>
                  <p className="text-sm text-slate-200">{e.service}</p>
                  {e.headcount && <p className="text-xs text-slate-400">{e.headcount}</p>}
                  {e.message && <p className="text-xs text-slate-500 mt-1">{e.message}</p>}
                </div>
                <div className="sm:text-right">
                  <p className="text-xs text-slate-500">{fmtDate(e.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="admin-applications-list" className="space-y-3">
            {applications.length === 0 && !loading && <Empty label="No job applications yet. Rider and worker applications will appear here." />}
            {applications.map((a) => (
              <div key={a.id} data-testid={`application-card-${a.id}`} className="bg-[#0d1424] border border-white/10 rounded-sm p-5 grid sm:grid-cols-4 gap-3">
                <div>
                  <p className="text-sm font-bold text-slate-100">{a.name}</p>
                  <p className="text-xs text-slate-500">{a.city}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Contact</p>
                  <a href={`tel:${a.phone}`} className="text-sm text-[#3b82f6]">{a.phone}</a>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Role</p>
                  <p className="text-sm text-slate-200">{a.role}</p>
                  <p className="text-xs text-slate-400">
                    Aadhar: {a.aadhar_verified === "yes" ? "Available" : "Not Available"}
                    {a.experience ? ` · ${a.experience}` : ""}
                  </p>
                  {a.message && <p className="text-xs text-slate-500 mt-1">{a.message}</p>}
                </div>
                <div className="sm:text-right">
                  <p className="text-xs text-slate-500">{fmtDate(a.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
